import EventEmitter from './events/EventEmitter';
import { TextAreaContent } from './quill/converters/types';
import { ISelectionRange, ITextAttributes } from './quill/types';
import { IRichTextFieldConverters } from './RichTextField/IRichTextFieldOptions';

export default abstract class IRichTextField extends EventEmitter {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public static setConverters(richTextFieldConverters: Partial<IRichTextFieldConverters>) { }

    /**
     * Focuses the editor and restores its last range.
     */
    public abstract focus(): void;

    /**
     * Removes focus from the editor.
     */
    public abstract blur(): void;

    /**
     * Enable or disable user input on this editor
     * @param enabled - set to false to disable user input
     */
    public abstract enable(enabled: boolean): void;

    /**
     * Format text at user’s current selection,
     * or the entire editor if no selection exists
     *
     * @param {string} attribute The attribute to be updated.
     * @param {any} value The new attribute value.
     */
    public abstract setAttribute(attribute: string, value: any): void;

    /**
     * Format text at user’s current selection,
     * or the entire editor if no selection exists
     *  @param {ITextAttributes} attributes The attributes to be updated.
    */
    public abstract setAttributes(attributes: ITextAttributes): void;

    /**
     * Retrieves common formatting of the text in the current selection range.
     *
     * @return The formatted attributes object
     */
    public abstract getCommonAttributes(): {[key: string]: any};

    /**
     * Retrieves all formatting of the text in the current selection range.
     *
     * @return The formatted attributes object
     */
    public abstract getAllAttributes(): {[key: string]: any[]};

    /**
     * Set the rich text content from CimDoc text area's content.
     *
     * @param {TextAreaContent[]} content The source text fields
     */
    public abstract setTextAreaContent(content: TextAreaContent[]): void;

    /**
     * Retrieves the rich text content as CimDoc text area contents.
     *
     * @return {TextAreaContent[]} The CimDoc text area contents
     */
    public abstract getTextAreaContent(): TextAreaContent[];

    /**
     * Sets the user's selection to a given range.
     *
     * @param {ISelectionRange | null} selection The new selection range
     */
    public abstract setSelection(selection: ISelectionRange | null): void;

    /**
     * Retrieves the user’s selection range.
     *
     * @return {ISelectionRange | null} The current selection range. Otherwise null may be returned if editor does not have focus.
     */
    public abstract getSelection(): ISelectionRange | null;

    /**
     * Retrieves the formatted text content for a specified range as CimDoc **TextAreaContent[]**.
     *
     * Extracts and converts the Quill delta for the given range into CimDoc's **TextAreaContent[]**.
     * If the selection does not end with a block-ending operation (`\n`), the next block-ending
     * operation is appended to ensure proper formatting.
     *
     * @param range - The range specifying the start index and length of the content to extract.
     * @returns An array of `TextAreaContent` representing the formatted text content for the specified range.
     */
      public abstract getRangeTextAreaContent(range: ISelectionRange): TextAreaContent[];

    /**
     * Retrieves the length of the editor contents.
     *
     * @return {number} The length
     */
    public abstract getLength(): number;

    /**
     * Inserts text into the editor at a given index,
     * or onto the end if no index is given
     *
     * @param {string} text The text to be inserted
     * @param {number} index The desired index
     */

    public abstract insert(text: string, index: number): void;
    /**
     * Deletes a given length of text from the editor at a given index,
     * or from the end if no index is given
     *
     * @param {number} length The amount of characters to delete
     * @param {number} index The desired index
     */

    public abstract delete(length: number, index: number): void;

    /**
     * Sets the placeholder data attribute for the text field.
     *
     * @param {string} placeholder The new placeholder text
     */
    public abstract updatePlaceholder(placeholder: string): void;

    /**
     * Force the underlying implementation to rerender the editor.
     */
    // eslint-disable-next-line class-methods-use-this
    public rerender(): void {
        // no-op by default. Implement if needed.
    }
}
